import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, BookOpen, CheckCircle2, Circle, Play, Square, Clock, User, Paperclip } from 'lucide-react';

interface HomeworkFile {
  url?: string;
  href?: string;
  path?: string;
  name?: string;
  label?: string;
  filename?: string;
  mime?: string;
  size?: number;
}

interface Homework {
  id: string;
  external_id: string;
  title: string;
  due_at: string | null;
  teacher: string | null;
  back: boolean;
  files_json: HomeworkFile[] | null;
  started_at: string | null;
  completed_at: string | null;
  total_work_seconds: number;
  active_session_started_at: string | null;
}

function fileUrl(f: HomeworkFile): string | null {
  return f.url || f.href || f.path || null;
}

function fileName(f: HomeworkFile): string {
  return f.name || f.label || f.filename || fileUrl(f)?.split('/').pop() || 'Datei';
}

function formatBytes(n?: number): string {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function FileLinks({ files }: { files: HomeworkFile[] | null }) {
  if (!files || files.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {files.map((f, i) => {
        const url = fileUrl(f);
        if (!url) return null;
        const size = formatBytes(f.size);
        return (
          <a key={i} href={url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/40 text-blue-200 px-2 py-1 rounded text-xs">
            <Paperclip className="w-3 h-3" />
            <span className="truncate max-w-[180px]">{fileName(f)}</span>
            {size && <span className="text-blue-300/70">({size})</span>}
          </a>
        );
      })}
    </div>
  );
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function Timer({ start }: { start: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const sec = Math.max(0, Math.floor((now - new Date(start).getTime()) / 1000));
  return <span className="text-yellow-300 font-mono text-xs">{formatDuration(sec)}</span>;
}

const HausaufgabenPage: React.FC = () => {
  const [items, setItems] = useState<Homework[]>([]);
  const [includeDone, setIncludeDone] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ homework: Homework[] }>(`/api/homework${includeDone ? '?include_done=1' : ''}`);
      setItems(r.homework);
    } catch (err: any) { setError(err?.message || 'Fehler'); }
  }, [includeDone]);

  useEffect(() => { load(); }, [load]);

  const toggleComplete = async (h: Homework) => {
    setBusy(h.id);
    try {
      await api(`/api/homework/${h.id}/complete`, {
        method: 'POST', body: JSON.stringify({ completed: !h.completed_at }),
      });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  const toggleTimer = async (h: Homework) => {
    setBusy(h.id);
    try {
      await api(`/api/homework/${h.id}/${h.active_session_started_at ? 'stop' : 'start'}`, { method: 'POST' });
      await load();
    } catch (err: any) { setError(err?.message || 'Fehler'); }
    finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <BookOpen className="w-6 h-6 text-blue-400" />
        <h1 className="text-xl font-semibold">Hausaufgaben</h1>
        <label className="ml-auto text-sm text-gray-400 flex items-center gap-2">
          <input type="checkbox" checked={includeDone} onChange={e => setIncludeDone(e.target.checked)} className="accent-blue-500" />
          Erledigte anzeigen
        </label>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6">
        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
        {items.length === 0 ? (
          <p className="text-gray-500 text-sm">Keine offenen Hausaufgaben. Sync das Schul-Portal auf dem Dashboard.</p>
        ) : (
          <ul className="space-y-3">
            {items.map(h => (
              <li key={h.id} className={`bg-gray-800 border border-gray-700 rounded-lg p-4 flex items-start gap-3 ${h.completed_at ? 'opacity-60' : ''}`}>
                <button onClick={() => toggleComplete(h)} disabled={busy === h.id} className="mt-1">
                  {h.completed_at
                    ? <CheckCircle2 className="w-5 h-5 text-green-400" />
                    : <Circle className="w-5 h-5 text-gray-500 hover:text-gray-300" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`whitespace-pre-wrap ${h.completed_at ? 'line-through text-gray-400' : ''}`}>{h.title}</div>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
                    {h.due_at && <span>📅 Fällig: {new Date(h.due_at).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>}
                    {h.teacher && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {h.teacher}</span>}
                    {h.total_work_seconds > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(h.total_work_seconds)}</span>}
                    {h.active_session_started_at && <Timer start={h.active_session_started_at} />}
                  </div>
                  <FileLinks files={h.files_json} />
                </div>
                <div className="flex items-center gap-2">
                  {!h.completed_at && (
                    <button onClick={() => toggleTimer(h)} disabled={busy === h.id}
                      className={`p-2 rounded-lg transition ${h.active_session_started_at ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-700 hover:bg-gray-600'}`}
                      title={h.active_session_started_at ? 'Timer stoppen' : 'Timer starten'}>
                      {h.active_session_started_at ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};

export default HausaufgabenPage;
